import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/ui/Button';
import { generateGoldenSolution } from '../../services/goldenSolutionService';
import GlobalNavigationMenu from '../../components/GlobalNavigationMenu';
import useTrialGuard from '../../hooks/useTrialGuard';
import TrialPaywall from '../../components/TrialPaywall';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

export default function GoldenSolutionPage() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    // BLOCO 1
    meuNome: '',
    sexo: '',
    idade: '',
    paisAtual: '',
    cidadeAtual: '',
    setorVive: '',
    enderecoDigitadoExtenso: '',
    minhaSituacao: '',
    minhaAreaProfissional: '',
    ocupacaoPrincipal: '',
    
    // NEW BLOCK - A Área que mais encaixa o meu problema é
    areaPrincipal: [],
    problemaSaude: '',
    tipoSaudeAfetada: [],
    relacaoAreaSaude: [],
    
    // BLOCO 2 - NEW: Descrição Resumida
    descricaoResumida: '',
    
    // BLOCO 2.1 - NEW: Objetivo
    objetivoSolucao: '',
    
    // BLOCO 3
    problemaRelacionado: '',
    ondeAplicar: '',
    descricaoAplicacaoSolucao: '',
    
    // BLOCO 5
    descricaoProblema: '',
    
    // NEW: Checkbox obrigatório
    quero_solucao_de_ouro: false
  });
  const { trialBlocked, setTrialBlocked, checkoutLoading, checkoutError, setCheckoutError, checkBeforeAI, incrementAfterAI, handleAssinarPro } = useTrialGuard('golden_solution');

  // PRO plan check
  const [isPro, setIsPro] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    const fetchPlan = async () => {
      if (!user) return;
      try {
        const { data } = await supabase?.from('profiles')?.select('plan')?.eq('id', user?.id)?.single();
        if (data?.plan === 'pro') setIsPro(true);
      } catch {}
    };
    fetchPlan();
  }, [user]);

  // 🔹 ETAPA 4-B: Load stored form data on mount (for "Voltar e Editar" functionality)
  useEffect(() => {
    const storedFormData = localStorage.getItem('golden_solution_form_data');
    if (storedFormData) {
      try {
        const parsedData = JSON.parse(storedFormData);
        setFormData(parsedData);
      } catch (error) {
        console.error('Error loading stored form data:', error);
      }
    }
  }, []);

  // 🔹 ETAPA 4-A: Effect to clear health fields when problemaSaude changes to 'nao'
  useEffect(() => {
    if (formData?.problemaSaude === 'nao') {
      setFormData(prev => ({
        ...prev,
        relacaoAreaSaude: [],
        tipoSaudeAfetada: []
      }));
    }
  }, [formData?.problemaSaude]);

  // 🔹 Determine if relacaoAreaSaude and tipoSaudeAfetada should be visible
  const shouldShowRelacaoAreaSaude = () => {
    return formData?.problemaSaude === 'sim';
  };

  // 🔹 ETAPA 4-A: Enhanced validation with conditional requirements
  const isFormValid = () => {
    // BLOCO 1 - Always required
    if (!formData?.meuNome || !formData?.sexo || !formData?.idade || !formData?.paisAtual || !formData?.cidadeAtual || !formData?.setorVive || !formData?.enderecoDigitadoExtenso || formData?.enderecoDigitadoExtenso?.trim() === '' || !formData?.minhaSituacao || !formData?.minhaAreaProfissional || !formData?.ocupacaoPrincipal) {
      return false;
    }

    // NEW BLOCK - Validation
    if (!formData?.areaPrincipal || formData?.areaPrincipal?.length === 0 || !formData?.problemaSaude) {
      return false;
    }
    
    // 🔹 ETAPA 4-A: Conditional validation for health fields
    if (formData?.problemaSaude === 'sim') {
      // When health problem = yes, require relacaoAreaSaude and tipoSaudeAfetada
      if (!formData?.relacaoAreaSaude || formData?.relacaoAreaSaude?.length === 0) {
        return false;
      }
      if (!formData?.tipoSaudeAfetada || formData?.tipoSaudeAfetada?.length === 0) {
        return false;
      }
    }

    // BLOCO 2 - Descrição Resumida is required
    if (!formData?.descricaoResumida || formData?.descricaoResumida?.trim() === '') {
      return false;
    }

    // BLOCO 2.1 - Objetivo is required
    if (!formData?.objetivoSolucao || formData?.objetivoSolucao?.trim() === '') {
      return false;
    }

    // BLOCO 3 - Always required
    if (!formData?.problemaRelacionado || !formData?.ondeAplicar) {
      return false;
    }
    
    if (!formData?.descricaoAplicacaoSolucao || formData?.descricaoAplicacaoSolucao?.trim() === '') {
      return false;
    }

    // BLOCO 5 - Always required
    if (!formData?.descricaoProblema || formData?.descricaoProblema?.trim() === '') {
      return false;
    }

    // NEW: Checkbox obrigatório validation
    if (!formData?.quero_solucao_de_ouro) {
      return false;
    }

    return true;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e?.target;
    
    if (type === 'checkbox') {
      // Handle array checkboxes (multiple selections)
      if (name === 'areaPrincipal' || name === 'tipoSaudeAfetada' || name === 'relacaoAreaSaude') {
        setFormData(prev => ({
          ...prev,
          [name]: checked 
            ? [...(prev?.[name] || []), value]
            : (prev?.[name] || [])?.filter(item => item !== value)
        }));
      } else {
        // Handle single boolean checkbox (quero_solucao_de_ouro)
        setFormData(prev => ({
          ...prev,
          [name]: checked
        }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // 🔹 ETAPA 4-A: Normalize "Ambos" fields to arrays
  const normalizeProblemaRelacionado = (value) => {
    if (value === 'vida-pessoal') return ['vida_pessoal'];
    if (value === 'trabalho-empresa') return ['trabalho_empresa'];
    if (value === 'ambos') return ['vida_pessoal', 'trabalho_empresa'];
    return [];
  };

  const normalizeOndeAplicar = (value) => {
    if (value === 'vida-pessoal') return ['vida_pessoal'];
    if (value === 'vida-profissional') return ['vida_profissional'];
    if (value === 'ambos') return ['vida_pessoal', 'vida_profissional'];
    return [];
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    // ── TRIAL GUARD ──
    const allowed = await checkBeforeAI();
    if (!allowed) return;
    // ── END TRIAL GUARD ──

    // 🔹 ETAPA 4-A: Build structured JSON payload
    const golden_solution_payload = {
      perfil: {
        meu_nome: formData?.meuNome,
        meu_sexo: formData?.sexo,
        minha_idade: Number(formData?.idade),
        meu_pais_atual: formData?.paisAtual,
        minha_cidade_atual: formData?.cidadeAtual,
        meu_setor_onde_vivo_ou_atuo: formData?.setorVive,
        meu_endereco_extenso: formData?.enderecoDigitadoExtenso,
        minha_situacao: formData?.minhaSituacao,
        minha_area_profissional_ou_ocupacao: formData?.minhaAreaProfissional,
        descricao_principal_ocupacao: formData?.ocupacaoPrincipal
      },
      area_e_saude: {
        areas_principais_do_problema: formData?.areaPrincipal,
        eh_problema_de_saude: formData?.problemaSaude === 'sim',
        relacao_area_saude: formData?.relacaoAreaSaude || [],
        tipos_de_saude_afetada: formData?.tipoSaudeAfetada || []
      },
      resumos: {
        descricao_resumida_do_problema: formData?.descricaoResumida,
        objetivo_ao_resolver: formData?.objetivoSolucao
      },
      sobre_o_problema: {
        problema_relacionado_a: normalizeProblemaRelacionado(formData?.problemaRelacionado),
        aplicar_solucao_em: normalizeOndeAplicar(formData?.ondeAplicar),
        onde_exatamente_aplicar: formData?.descricaoAplicacaoSolucao
      },
      descricao_detalhada: {
        descricao_ampla_detalhada: formData?.descricaoProblema
      },
      quero_solucao_de_ouro: formData?.quero_solucao_de_ouro
    };
    
    // 🔹 ETAPA 4-A: Debug console.log
    console.log('golden_solution_payload', golden_solution_payload);
    
    // 🔹 ETAPA 4-B: Store form data for "Voltar e Editar" functionality
    localStorage.setItem('golden_solution_form_data', JSON.stringify(formData));
    
    try {
      // 🔹 ETAPA 4-B: Set loading state and disable button
      setIsSubmitting(true);
      
      // 🔹 ETAPA 4-B: Call OpenAI API to generate solution
      const aiResponse = await generateGoldenSolution(golden_solution_payload);
      
      // ── INCREMENT USAGE ──
      await incrementAfterAI();
      // ── END INCREMENT ──

      // 🔹 ETAPA 4-B: Store AI response in localStorage
      localStorage.setItem('golden_solution_response', aiResponse);
      
      // 🔹 ETAPA 4-B: Redirect to result page
      navigate('/golden-solution-result');
    } catch (error) {
      console.error('Error generating solution:', error);
      alert(error?.message || 'Erro ao gerar solução. Por favor, tente novamente.');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <main className="p-4 lg:p-8">
        <div className="max-w-4xl mx-auto">
          {/* Header Section */}
          <div className="mb-8">
            <div className="flex items-center justify-center mb-4">
              <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-full flex items-center justify-center shadow-lg">
                <span className="text-3xl">⭐</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <GlobalNavigationMenu />
                <div className="flex items-center gap-2">
                  <h1 className="text-3xl lg:text-4xl font-bold text-slate-900">
                    Solução de Ouro
                  </h1>
                </div>
              </div>
            </div>
            <p className="text-base lg:text-lg text-slate-600 max-w-2xl mx-auto text-center">
              Encontre a melhor solução para o seu problema, com clareza e custo-benefício.
            </p>
          </div>

          {/* Main Container */}
          <div className="bg-white rounded-xl shadow-md border border-slate-200 p-6 lg:p-8">
            <form onSubmit={handleSubmit} className="space-y-8">
              
              {/* BLOCO 1 - Meu Perfil */}
              <div className="border-l-4 border-blue-500 pl-4">
                <h2 className="text-xl font-semibold text-slate-800 mb-4">
Meu Perfil
                </h2>
                <div className="space-y-4">
                  {/* Meu Nome */}
                  <div>
                    <label htmlFor="meuNome" className="block text-sm font-medium text-slate-700 mb-1">
                      Meu Nome <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="meuNome"
                      name="meuNome"
                      required
                      placeholder="Ex: João Silva"
                      value={formData?.meuNome}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  {/* Sexo */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Meu Sexo <span className="text-red-500">*</span>
                    </label>
                    <div className="space-y-2">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="sexo"
                          value="masculino"
                          checked={formData?.sexo === 'masculino'}
                          onChange={handleInputChange}
                          className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-slate-700">Masculino</span>
                      </label>
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="sexo"
                          value="feminino"
                          checked={formData?.sexo === 'feminino'}
                          onChange={handleInputChange}
                          className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-slate-700">Feminino</span>
                      </label>
                    </div>
                  </div>

                  {/* Idade */}
                  <div>
                    <label htmlFor="idade" className="block text-sm font-medium text-slate-700 mb-1">
                      Minha Idade <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      id="idade"
                      name="idade"
                      required
                      placeholder="Ex: 32"
                      value={formData?.idade}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  {/* País Atual */}
                  <div>
                    <label htmlFor="paisAtual" className="block text-sm font-medium text-slate-700 mb-1">
                      Meu País atual <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="paisAtual"
                      name="paisAtual"
                      required
                      value={formData?.paisAtual}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  {/* Cidade Atual */}
                  <div>
                    <label htmlFor="cidadeAtual" className="block text-sm font-medium text-slate-700 mb-1">
                      Minha Cidade atual <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="cidadeAtual"
                      name="cidadeAtual"
                      required
                      value={formData?.cidadeAtual}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  {/* Setor onde vive */}
                  <div>
                    <label htmlFor="setorVive" className="block text-sm font-medium text-slate-700 mb-1">
                      Meu Setor onde vivo ou atuo <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="setorVive"
                      name="setorVive"
                      required
                      placeholder="Ex: Centro, Zona Sul, Bairro X"
                      value={formData?.setorVive}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  {/* Meu Endereço digitado por extenso */}
                  <div>
                    <label htmlFor="enderecoDigitadoExtenso" className="block text-sm font-medium text-slate-700 mb-1">
                      Meu Endereço digitado por extenso (Apenas País, Cidade e Setor/Bairro) <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="enderecoDigitadoExtenso"
                      name="enderecoDigitadoExtenso"
                      required
                      placeholder="Ex.: Brasil, Goiânia, Setor Bueno"
                      value={formData?.enderecoDigitadoExtenso}
                      onChange={handleInputChange}
                      rows={2}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    />
                  </div>

                  {/* Minha situação */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Minha Situação <span className="text-red-500">*</span>
                    </label>
                    <div className="space-y-2">
                      {[
                        { value: 'proprietario', label: 'Proprietário(a) de empresa / Empreendedor(a)' },
                        { value: 'funcionario', label: 'Funcionário(a)' },
                        { value: 'autonomo', label: 'Autônomo(a)' },
                        { value: 'estudante', label: 'Estudante/Aluno(a)' },
                        { value: 'professor', label: 'Professor(a)' },
                        { value: 'outro', label: 'Outro' }
                      ]?.map(option => (
                        <label key={option?.value} className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="radio"
                            name="minhaSituacao"
                            value={option?.value}
                            required
                            checked={formData?.minhaSituacao === option?.value}
                            onChange={handleInputChange}
                            className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm text-slate-700">{option?.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Minha área profissional */}
                  <div>
                    <label htmlFor="minhaAreaProfissional" className="block text-sm font-medium text-slate-700 mb-1">
                      Minha Área Profissional ou Minha Ocupação <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="minhaAreaProfissional"
                      name="minhaAreaProfissional"
                      required
                      value={formData?.minhaAreaProfissional}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Selecionar Minha Área Profissional ou Minha Ocupação</option>
                      <option value="saude">Sou Profissional da Área da Saúde</option>
                      <option value="educacao">Sou Profissional da Área da Educação</option>
                      <option value="ti">Sou Profissional da Área da Tecnologia da Informação (TI)</option>
                      <option value="engenharia">Sou Profissional da Área da Engenharia</option>
                      <option value="direito">Sou Profissional da Área da Direito / Jurídica</option>
                      <option value="administracao">Sou Profissional da Área da Administração e Gestão</option>
                      <option value="financas">Sou Profissional da Área da Finanças e Contabilidade</option>
                      <option value="marketing">Sou Profissional da Área da Marketing e Publicidade</option>
                      <option value="vendas">Sou Profissional da Área da Vendas e Comercial</option>
                      <option value="rh">Sou Profissional da Área da Recursos Humanos</option>
                      <option value="comunicacao">Sou Profissional da Área da Comunicação e Jornalismo</option>
                      <option value="design">Sou Profissional da Área da Design e Criação</option>
                      <option value="artes">Sou Profissional da Área da Artes e Cultura</option>
                      <option value="audiovisual">Sou Profissional da Área da Audiovisual e Multimídia</option>
                      <option value="moda">Sou Profissional da Área da Moda e Beleza</option>
                      <option value="arquitetura">Sou Profissional da Área da Arquitetura e Urbanismo</option>
                      <option value="construcao">Sou Profissional da Área da Construção Civil</option>
                      <option value="industria">Sou Profissional da Área da Indústria e Produção</option>
                      <option value="logistica">Sou Profissional da Área da Logística e Transportes</option>
                      <option value="comercio">Sou Profissional da Área da Comércio e Varejo</option>
                      <option value="empreendedorismo">Sou Profissional da Área da Empreendedorismo e Negócios</option>
                      <option value="agronegocio">Sou Profissional da Área da Agronegócio e Meio Rural</option>
                      <option value="meio-ambiente">Sou Profissional da Área da Meio Ambiente e Sustentabilidade</option>
                      <option value="turismo">Sou Profissional da Área da Turismo e Hotelaria</option>
                      <option value="gastronomia">Sou Profissional da Área da Gastronomia e Alimentação</option>
                      <option value="esportes">Sou Profissional da Área da Esportes e Educação Física</option>
                      <option value="psicologia">Sou Profissional da Área da Psicologia e Desenvolvimento Humano</option>
                      <option value="ciencias-humanas">Sou Profissional da Área da Ciências Humanas e Sociais</option>
                      <option value="ciencias-exatas">Sou Profissional da Área da Ciências Exatas</option>
                      <option value="ciencias-biologicas">Sou Profissional da Área da Ciências Biológicas</option>
                      <option value="seguranca">Sou Profissional da Área da Segurança Pública e Privada</option>
                      <option value="defesa">Sou Profissional da Área da Defesa e Forças Armadas</option>
                      <option value="religiao">Sou Profissional da Área da Religião e Espiritualidade</option>
                      <option value="pesquisa">Sou Profissional da Área da Pesquisa Científica e Acadêmica</option>
                      <option value="consultoria">Sou Profissional da Área da Consultoria e Mentoria</option>
                      <option value="atendimento">Sou Profissional da Área da Atendimento ao Cliente / Suporte</option>
                      <option value="servicos-gerais">Sou Profissional da Área da Serviços Gerais e Operacionais</option>
                      <option value="estudante-aluno">Sou Estudante/Aluno(a)</option>
                      <option value="autonomo-area">Sou Autônomo(a)</option>
                      <option value="proprietario-area">Sou Proprietário(a) de empresa/Empreendedor(a)</option>
                    </select>
                  </div>

                  {/* Ocupação Principal */}
                  <div>
                    <label htmlFor="ocupacaoPrincipal" className="block text-sm font-medium text-slate-700 mb-1">
                      Descrição da minha Principal Ocupação / Profissão / Cargo (em uma linha) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="ocupacaoPrincipal"
                      name="ocupacaoPrincipal"
                      required
                      placeholder="Ex: Analista de Marketing, Médico Clínico, Advogado, Estudante de Engenharia"
                      value={formData?.ocupacaoPrincipal}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* NEW BLOCK - A Área que mais encaixa o meu problema é */}
              <div className="border-l-4 border-indigo-500 pl-4">
                <h2 className="text-xl font-semibold text-slate-800 mb-4">
                  A(s) Área(s) que o Meu Problema Mais Se Encaixa É/São
                </h2>
                <div className="space-y-4">
                  {/* 1️⃣ Área principal do problema */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Área(s) Principal ou Principais do Problema <span className="text-red-500">*</span>
                    </label>
                    <div className="space-y-2">
                      {[
                        { value: 'pessoal-particular', label: 'Pessoal / Particular' },
                        { value: 'familiar', label: 'Familiar' },
                        { value: 'profissional-trabalho', label: 'Profissional / Trabalho' },
                        { value: 'empresarial', label: 'Empresarial' }
                      ]?.map(option => (
                        <label key={option?.value} className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            name="areaPrincipal"
                            value={option?.value}
                            checked={formData?.areaPrincipal?.includes(option?.value)}
                            onChange={handleInputChange}
                            className="w-4 h-4 text-indigo-600 focus:ring-indigo-500 rounded"
                          />
                          <span className="text-sm text-slate-700">{option?.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* 2️⃣ Pergunta de saúde */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
Esse Problema Afeta a Minha Saúde de Alguma Forma? <span className="text-red-500">*</span>
                    </label>
                    <div className="space-y-2">
                      {[
                        { value: 'sim', label: 'Sim' },
                        { value: 'nao', label: 'Não' }
                      ]?.map(option => (
                        <label key={option?.value} className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="radio"
                            name="problemaSaude"
                            value={option?.value}
                            required
                            checked={formData?.problemaSaude === option?.value}
                            onChange={handleInputChange}
                            className="w-4 h-4 text-indigo-600 focus:ring-indigo-500"
                          />
                          <span className="text-sm text-slate-700">{option?.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* 4️⃣ Relação área + saúde - 🔹 CONDITIONAL DISPLAY */}
                  {shouldShowRelacaoAreaSaude() && (
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Relação Área(s) + Saúde <span className="text-red-500">*</span>
                      </label>
                      <div className="space-y-2">
                        {[
                          { value: 'pessoal-afetando-saude', label: 'Esse problema é na área Pessoal/Particular e está afetando minha saúde' },
                          { value: 'familiar-afetando-saude', label: 'Esse problema é na área Familiar e está afetando minha saúde' },
                          { value: 'profissional-afetando-saude', label: 'Esse problema é na área Profissional/Trabalho e está afetando minha saúde' },
                          { value: 'empresarial-afetando-saude', label: 'Esse problema é na área Empresarial e está afetando minha saúde' },
                          { value: 'nao-afetando-saude', label: 'Esse problema não está afetando minha saúde' }
                        ]?.map(option => (
                          <label key={option?.value} className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              name="relacaoAreaSaude"
                              value={option?.value}
                              checked={formData?.relacaoAreaSaude?.includes(option?.value)}
                              onChange={handleInputChange}
                              className="w-4 h-4 text-indigo-600 focus:ring-indigo-500 rounded"
                            />
                            <span className="text-sm text-slate-700">{option?.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* 3️⃣ Tipo de saúde afetada - 🔹 CONDITIONAL DISPLAY */}
                  {shouldShowRelacaoAreaSaude() && (
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Tipo(s) de Saúde Afetada <span className="text-red-500">*</span>
                      </label>
                      <div className="space-y-2">
                        {[
                          { value: 'fisica', label: 'Saúde Física' },
                          { value: 'mental', label: 'Saúde Mental' },
                          { value: 'emocional', label: 'Saúde Emocional' }
                        ]?.map(option => (
                          <label key={option?.value} className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              name="tipoSaudeAfetada"
                              value={option?.value}
                              checked={formData?.tipoSaudeAfetada?.includes(option?.value)}
                              onChange={handleInputChange}
                              className="w-4 h-4 text-indigo-600 focus:ring-indigo-500 rounded"
                            />
                            <span className="text-sm text-slate-700">{option?.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* BLOCO 2 - NEW: Descrição Resumida do meu Problema */}
              <div className="border-l-4 border-emerald-500 pl-4">
                <h2 className="text-xl font-semibold text-slate-800 mb-4">
                  Descrição Resumida do meu Problema
                </h2>
                <div>
                  <textarea
                    id="descricaoResumida"
                    name="descricaoResumida"
                    required
                    rows={6}
                    placeholder="Escreva um resumo do seu problema aqui…"
                    value={formData?.descricaoResumida}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none"
                  />
                </div>
              </div>

              {/* BLOCO 2.1 - NEW: Objetivo */}
              <div className="border-l-4 border-teal-500 pl-4">
                <h2 className="text-xl font-semibold text-slate-800 mb-4">
                  O meu Objetivo ao Encontrar a Solução desse Problema é
                </h2>
                <div>
                  <label htmlFor="objetivoSolucao" className="block text-sm font-medium text-slate-700 mb-2">
                    Descrição Resumida sobre qual é o meu Objetivo ao Resolver esse Problema <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="objetivoSolucao"
                    name="objetivoSolucao"
                    required
                    rows={6}
                    placeholder="Ex.: economizar tempo, reduzir custos, melhorar resultados, encontrar um profissional confiável…"
                    value={formData?.objetivoSolucao}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
                  />
                </div>
              </div>

              {/* BLOCO 3 - Sobre o Problema */}
              <div className="border-l-4 border-purple-500 pl-4">
                <h2 className="text-xl font-semibold text-slate-800 mb-4">
Mais Informações
                </h2>
                <div className="space-y-4">
                  {/* O problema está relacionado a */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      O problema está relacionado a: <span className="text-red-500">*</span>
                    </label>
                    <div className="space-y-2">
                      {[
                        { value: 'vida-pessoal', label: 'Vida pessoal' },
                        { value: 'trabalho-empresa', label: 'Trabalho/empresa' },
                        { value: 'ambos', label: 'Vida pessoal e Trabalho/Empresa' }
                      ]?.map(option => (
                        <label key={option?.value} className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="radio"
                            name="problemaRelacionado"
                            value={option?.value}
                            checked={formData?.problemaRelacionado === option?.value}
                            onChange={handleInputChange}
                            className="w-4 h-4 text-blue-600 focus:ring-2 focus:ring-blue-500"
                          />
                          <span className="text-sm text-slate-700">{option?.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Onde você quer aplicar a solução? */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Onde você quer aplicar a solução? <span className="text-red-500">*</span>
                    </label>
                    <div className="space-y-2">
                      {[
                        { value: 'vida-pessoal', label: 'Vida Pessoal' },
                        { value: 'vida-profissional', label: 'Vida Profissional' },
                        { value: 'ambos', label: 'Vida Pessoal e Vida Profissional' }
                      ]?.map(option => (
                        <label key={option?.value} className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="radio"
                            name="ondeAplicar"
                            value={option?.value}
                            checked={formData?.ondeAplicar === option?.value}
                            onChange={handleInputChange}
                            className="w-4 h-4 text-blue-600 focus:ring-2 focus:ring-blue-500"
                          />
                          <span className="text-sm text-slate-700">{option?.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Descrição da Aplicação da Solução */}
                  <div>
                    <label htmlFor="descricaoAplicacaoSolucao" className="block text-sm font-medium text-slate-700 mb-2">
                      Descreva (em duas linhas) exatamente onde e em qual área da vida pretende aplicar essa solução <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="descricaoAplicacaoSolucao"
                      name="descricaoAplicacaoSolucao"
                      required
                      rows={2}
                      placeholder="Ex: Na minha carreira atual como gestor, dentro da empresa onde trabalho."
                      value={formData?.descricaoAplicacaoSolucao}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                    />
                  </div>
                </div>
              </div>

              {/* BLOCO 5 - Descrição Detalhada do Problema */}
              <div className="border-l-4 border-purple-500 pl-4">
                <h2 className="text-xl font-semibold text-slate-800 mb-4">
Descrição mais Detalhada e Ampla do meu Problema
                </h2>
                <div>
                  <label htmlFor="descricaoProblema" className="block text-sm font-medium text-slate-700 mb-2">
Descrição Ampla e Detalhada <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="descricaoProblema"
                    name="descricaoProblema"
                    required
                    rows={8}
                    placeholder="Descreva seu problema com detalhes e clareza"
                    value={formData?.descricaoProblema}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                  />
                </div>
              </div>

              {/* NEW: Checkbox Obrigatório - Quero uma Solução de Ouro */}
              <div className="border-l-4 border-amber-500 pl-4">
                <div className="space-y-2">
                  <label className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name="quero_solucao_de_ouro"
                      checked={formData?.quero_solucao_de_ouro}
                      onChange={handleInputChange}
                      className="w-5 h-5 text-amber-600 focus:ring-2 focus:ring-amber-500 rounded mt-0.5"
                    />
                    <span className="text-sm font-medium text-slate-700">
                      Quero uma Solução de Ouro para esse Problema <span className="text-red-500">*</span>
                    </span>
                  </label>
                  {!formData?.quero_solucao_de_ouro && (
                    <p className="text-sm text-red-600 ml-8">
                      Você precisa marcar essa opção para enviar o formulário.
                    </p>
                  )}
                </div>
              </div>

              {/* Submit Button */}
              <div className="pt-4 border-t border-slate-200">
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  fullWidth
                  disabled={!isFormValid() || isSubmitting}
                  className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-4 rounded-lg shadow-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:from-blue-600 disabled:hover:to-blue-700"
                >
                  {isSubmitting ? 'Gerando sua Solução de Ouro…' : 'Enviar Formulário'}
                </Button>
                <p className="mt-3 text-center text-xs text-slate-500">
                  Toda IA pode cometer erros. Confira informações importantes.
                </p>
              </div>
            </form>
          </div>
        </div>
      </main>
      {trialBlocked && !isPro && (
        <TrialPaywall
          onAssinarPro={handleAssinarPro}
          checkoutLoading={checkoutLoading}
          checkoutError={checkoutError}
          onDismissError={() => setCheckoutError(null)}
          onClose={() => setTrialBlocked(false)}
        />
      )}
    </div>
  );
}